// frontend/src/components/VideoPlayer.tsx - v2.1
// Changes:
// - Imported getDisplayFileName from FileUtils.ts
// - Renamed fileName state to displayFileName for clarity
// - Added originalFileName state to store the original file name for downloads
// - Updated displayFileName to use getDisplayFileName for UI display
// - Ensured originalFileName is used for downloads to preserve the original name
// - Updated video element title to use displayFileName

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  getPresignedViewUrl, 
  getPresignedDownloadUrl, 
  FileType, 
  getFileName,
  apiLogger
} from '../services/api';
import { getDisplayFileName, getFileTypeFromExtension } from '../services/FileUtils';
import { recordActivity } from '../services/activityService';
import SpeedDownloadDialog from './SpeedDownloadDialog';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Container, 
  Stack, 
  Breadcrumbs,
  Alert,
  CircularProgress,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';

const PLAYBACK_SPEEDS = [0.5, 1, 1.5, 2, 3, 4, 6, 8, 10, 12];

const COMPONENT_CONTEXT = 'VideoPlayer';



// Immediate redirect component
const ImmediateRedirectRouter: React.FC = () => {
  const { key } = useParams<{ key: string }>();
  const decodedKey = key ? decodeURIComponent(key) : '';
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!decodedKey) return;
    
    // Detect file type
    const detectedType = getFileTypeFromExtension(decodedKey);
    
    // Immediately redirect for non-video files
    if (detectedType !== FileType.VIDEO) {
      apiLogger.info('VideoPlayer', `Detected non-video file type (${detectedType}) for: ${decodedKey}, redirecting immediately...`);
      
      if (detectedType === FileType.CSV) {
        navigate(`/view/csv/${encodeURIComponent(decodedKey)}`, { replace: true });
      } else if (detectedType === FileType.TEXT) {
        navigate(`/view/text/${encodeURIComponent(decodedKey)}`, { replace: true });
      } else if (detectedType === FileType.IMAGE) {
        navigate(`/view/image/${encodeURIComponent(decodedKey)}`, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [decodedKey, navigate]);
  
  // Show loading indicator while checking/redirecting
  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Checking file type...
        </Typography>
      </Box>
    </Container>
  );
};

const VideoPlayer: React.FC = () => {
  const { key } = useParams<{ key: string }>();
  const decodedKey = key ? decodeURIComponent(key) : '';
  const navigate = useNavigate();
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  // Tracks automatic recovery attempts after a playback error. Capped at one retry so a
  // genuinely missing/corrupt file does not loop forever (each failed load re-fires onError).
  const autoRetryCountRef = useRef(0);
  const MAX_AUTO_RETRIES = 1;
  
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [displayFileName, setDisplayFileName] = useState<string>(getDisplayFileName(decodedKey)); // Use getDisplayFileName for display
  const [originalFileName, setOriginalFileName] = useState<string>(getFileName(decodedKey)); // Store original file name for downloads
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileType, setFileType] = useState<FileType | string | null>(null);
  const [mimeType, setMimeType] = useState<string | null>(null);
  const [videoError, setVideoError] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [videoDuration, setVideoDuration] = useState(0);
  const [speedDownloadOpen, setSpeedDownloadOpen] = useState(false);

  const handleSpeedChange = useCallback((_: React.MouseEvent<HTMLElement>, value: number | null) => {
    if (value === null) return; // keep current selection if clicking the active button
    setPlaybackRate(value);
    if (videoRef.current) {
      videoRef.current.playbackRate = value;
    }
  }, []);

  // Re-apply rate after src changes (browser resets it on load); also capture duration
  const handleVideoLoadedMetadata = useCallback(() => {
    // A successful load means the current URL is valid; reset the auto-retry budget so a
    // future expiry (e.g. the user leaves the tab open past the token lifetime) can recover.
    autoRetryCountRef.current = 0;
    if (videoRef.current) {
      if (playbackRate !== 1) videoRef.current.playbackRate = playbackRate;
      if (videoRef.current.duration && isFinite(videoRef.current.duration)) {
        setVideoDuration(videoRef.current.duration);
      }
    }
  }, [playbackRate]);

  // Lifecycle logging and cleanup
  useEffect(() => {
    apiLogger.info(COMPONENT_CONTEXT, `Mounted player for file: ${decodedKey}`);
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
      apiLogger.info(COMPONENT_CONTEXT, `Unmounted player for file: ${decodedKey}`);
    };
  }, [decodedKey]);

  // Load the view presigned URL only — single API call on mount.
  // Download URL is fetched lazily on first click via fetchDownloadUrl.
  // File type is derived from the response or from the key extension via getFileTypeFromExtension.
  const fetchPresignedUrls = useCallback(async () => {
    if (!decodedKey) {
      setError('No file specified');
      setIsLoading(false);
      return;
    }

    try {
      apiLogger.info(COMPONENT_CONTEXT, `Fetching presigned URL for key: ${decodedKey}`);
      // No cacheBuster here — allows the cache populated by hover pre-fetch to be hit.
      // The retry handler passes cacheBuster to force a fresh URL after a playback error.
      const streamResponse = await getPresignedViewUrl(decodedKey, 7200);
      setVideoUrl(streamResponse.url);
      setDisplayFileName(getDisplayFileName(decodedKey));
      setOriginalFileName(streamResponse.filename || getFileName(decodedKey));

      const resolvedFileType = streamResponse.fileType || getFileTypeFromExtension(decodedKey);
      setFileType(resolvedFileType);

      // Record activity — only for video files that will actually play
      if (!resolvedFileType || resolvedFileType === FileType.VIDEO) {
        recordActivity(decodedKey, 'played', FileType.VIDEO);
      }

      if (streamResponse.mimeType) {
        setMimeType(streamResponse.mimeType);
      }

      if (resolvedFileType && resolvedFileType !== FileType.VIDEO) {
        setError(`This file is a ${String(resolvedFileType).toLowerCase()} file and cannot be played in the video player.`);
        setRedirecting(true);
        redirectTimeoutRef.current = setTimeout(() => {
          if (resolvedFileType === FileType.CSV) {
            navigate(`/view/csv/${encodeURIComponent(decodedKey)}`);
          } else if (resolvedFileType === FileType.TEXT) {
            navigate(`/view/text/${encodeURIComponent(decodedKey)}`);
          } else if (resolvedFileType === FileType.IMAGE) {
            navigate(`/view/image/${encodeURIComponent(decodedKey)}`);
          } else {
            navigate('/');
          }
        }, 3000);
      }
    } catch (err) {
      apiLogger.error(COMPONENT_CONTEXT, `Error fetching presigned URL: ${err}`, err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(`Failed to load video: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  }, [decodedKey, navigate]);

  useEffect(() => {
    fetchPresignedUrls();
  }, [fetchPresignedUrls]);

  // Lazily fetch the download URL on first click; subsequent clicks use the cached value.
  const fetchDownloadUrl = useCallback(async (): Promise<string> => {
    if (downloadUrl) return downloadUrl;
    const response = await getPresignedDownloadUrl(decodedKey);
    setDownloadUrl(response.url);
    return response.url;
  }, [decodedKey, downloadUrl]);

  const handleDownload = useCallback(async () => {
    try {
      setIsDownloading(true);
      apiLogger.info(COMPONENT_CONTEXT, `Downloading file: ${decodedKey}, Original name: ${originalFileName}`);
      recordActivity(decodedKey, 'downloaded', FileType.VIDEO);
      const url = await fetchDownloadUrl();
      const link = document.createElement('a');
      link.href = url;
      link.download = originalFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      apiLogger.error(COMPONENT_CONTEXT, `Download failed: ${error}`, error);
      setError(`Download failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsDownloading(false);
    }
  }, [fetchDownloadUrl, originalFileName, decodedKey]);

  // Probe the current presigned URL with a 1-byte ranged GET to determine whether a
  // playback failure is caused by an expired/forbidden S3 link (HTTP 403/401) rather
  // than an actual codec problem. The browser reports both cases as the same
  // MEDIA_ERR_SRC_NOT_SUPPORTED, so this disambiguates the user-facing message.
  // Returns true if the URL is no longer accessible (expired/forbidden).
  const isUrlExpired = useCallback(async (url: string | null): Promise<boolean> => {
    if (!url) return false;
    try {
      // Range bytes=0-0 keeps this to a single byte and matches the signed GetObject op.
      const res = await fetch(url, { headers: { Range: 'bytes=0-0' } });
      return res.status === 403 || res.status === 401;
    } catch {
      // Network/CORS failure is inconclusive — don't claim the link expired.
      return false;
    }
  }, []);

  // Discard the cached (possibly expired) presigned URL, fetch a fresh one from the Worker,
  // and re-trigger playback via the <video> element's .load(). Shared by the automatic
  // recovery path (handleVideoError) and the manual "Retry Video" button.
  const refreshUrlAndReload = useCallback(async () => {
    setVideoError(false);
    setError(null);
    setIsRetrying(true);
    apiLogger.info(COMPONENT_CONTEXT, `Refreshing presigned URL and reloading video for: ${decodedKey}`);

    try {
      // forceRefresh bypasses the in-memory/sessionStorage cache so we never re-serve the
      // expired URL that just produced the 403/MEDIA_ERR.
      const streamResponse = await getPresignedViewUrl(decodedKey, 7200, { forceRefresh: true });
      setVideoUrl(streamResponse.url);

      if (videoRef.current) {
        // load() forces the element to re-fetch the (now updated) src and restart loading.
        videoRef.current.load();
      }
    } catch (err) {
      apiLogger.error(COMPONENT_CONTEXT, `Error refreshing video URL: ${err}`, err);
      setError(`Failed to load video: ${err instanceof Error ? err.message : String(err)}`);
      setVideoError(true);
    } finally {
      setIsRetrying(false);
    }
  }, [decodedKey]);

  const handleVideoError = useCallback(async (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    const videoElement = e.currentTarget as HTMLVideoElement;
    const code = videoElement.error?.code;
    let errorMsg = 'Failed to load video. ';

    // Code 4 (MEDIA_ERR_SRC_NOT_SUPPORTED) is what the browser raises when it cannot parse
    // the resource — most commonly because S3 returned a 403 Forbidden XML body for an
    // expired presigned URL rather than an actual MP4. Auto-recover by fetching a fresh URL
    // and reloading. Capped at MAX_AUTO_RETRIES so a genuinely unsupported/missing file
    // doesn't loop (a failed reload re-fires onError).
    if (
      (code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED || code === MediaError.MEDIA_ERR_NETWORK) &&
      autoRetryCountRef.current < MAX_AUTO_RETRIES
    ) {
      autoRetryCountRef.current += 1;
      apiLogger.info(
        COMPONENT_CONTEXT,
        `Auto-retry ${autoRetryCountRef.current}/${MAX_AUTO_RETRIES} after MEDIA_ERR (code ${code}) for: ${decodedKey} — fetching fresh presigned URL`
      );
      await refreshUrlAndReload();
      return;
    }

    // SRC_NOT_SUPPORTED and NETWORK are the codes the browser raises when an S3 link
    // is rejected (403) — verify before blaming the video format.
    if (code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED || code === MediaError.MEDIA_ERR_NETWORK) {
      if (await isUrlExpired(videoUrl)) {
        errorMsg += 'The secure link to this video has expired. Click "Retry Video" to reload it.';
        apiLogger.error(COMPONENT_CONTEXT, `Video link expired (S3 403) for: ${decodedKey}`);
        setVideoError(true);
        setError(errorMsg);
        return;
      }
    }

    switch (code) {
      case MediaError.MEDIA_ERR_NETWORK:
        errorMsg += 'A network error occurred.';
        break;
      case MediaError.MEDIA_ERR_DECODE:
        errorMsg += 'The video format or codec may not be supported by your browser.';
        break;
      case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
        errorMsg += 'The video format is not supported.';
        break;
      default:
        errorMsg += 'An unknown error occurred.';
    }
    apiLogger.error(COMPONENT_CONTEXT, `Video playback error for: ${decodedKey} - ${errorMsg}`);
    setVideoError(true);
    setError(errorMsg);
  }, [decodedKey, videoUrl, isUrlExpired, refreshUrlAndReload]);

  // Manual retry from the "Retry Video" button. Resets the auto-retry budget so the user
  // can keep trying even after automatic recovery has been exhausted.
  const handleRetryVideo = useCallback(async () => {
    autoRetryCountRef.current = 0;
    await refreshUrlAndReload();
  }, [refreshUrlAndReload]);

  const handleBack = () => {
    navigate(-1); // Navigate back to the previous page in history
  };

  const cancelRedirect = useCallback(() => {
    setRedirecting(false);
    setError(null);
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
    }
    apiLogger.info(COMPONENT_CONTEXT, `Redirect canceled for: ${decodedKey}`);
  }, [decodedKey]);



  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
          <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Typography color="text.primary">{displayFileName}</Typography>
        </Breadcrumbs>

        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" component="h2">
              {displayFileName}
            </Typography>
            {fileType && (
              <Chip 
                icon={<PlayIcon />}
                label={String(fileType).toUpperCase()}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
          </Box>
          
          {decodedKey !== displayFileName && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Path: {decodedKey}
            </Typography>
          )}

          {/* File info chips — derived from the presigned URL response, no extra network call */}
          {(mimeType || fileType) && !isLoading && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {mimeType && (
                <Chip size="small" label={`Type: ${mimeType}`} variant="outlined" />
              )}
              {fileType && !mimeType && (
                <Chip size="small" label={`Type: ${String(fileType).toUpperCase()}`} variant="outlined" />
              )}
            </Box>
          )}

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {error}
              {(fileType === FileType.CSV || fileType === FileType.TEXT || fileType === FileType.OTHER) && redirecting && (
                <>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Redirecting you to the appropriate viewer...
                  </Typography>
                  <Button
                    onClick={cancelRedirect}
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1 }}
                    startIcon={<CancelIcon />}
                    aria-label="Cancel redirect"
                  >
                    Cancel
                  </Button>
                </>
              )}
              {videoError && (
                <Button 
                  onClick={handleRetryVideo} 
                  variant="outlined" 
                  size="small" 
                  sx={{ mt: 1, ml: redirecting ? 1 : 0 }}
                  startIcon={isRetrying ? <CircularProgress size={16} /> : <RefreshIcon />}
                  disabled={isRetrying}
                  aria-label="Retry loading video"
                >
                  Retry Video
                </Button>
              )}
            </Alert>
          ) : videoUrl ? (
            <Box 
              sx={{ 
                width: '100%', 
                borderRadius: 1, 
                overflow: 'hidden',
                mb: 3
              }}
            >
              <video 
                ref={videoRef}
                style={{ width: '100%', maxHeight: '70vh' }}
                controls 
                preload="metadata"
                src={videoUrl}
                controlsList="nodownload"
                onError={handleVideoError}
                onLoadedMetadata={handleVideoLoadedMetadata}
                playsInline
                autoPlay={false}
                title={`Video player for ${displayFileName}`}
              >
                Your browser does not support the video tag.
              </video>

              {/* Custom playback speed control — extends beyond the browser's native 2x cap */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, mb: 1, flexWrap: 'wrap' }}>
                <Tooltip title="Playback speed">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                    <SpeedIcon fontSize="small" />
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>Speed</Typography>
                  </Box>
                </Tooltip>
                <ToggleButtonGroup
                  value={playbackRate}
                  exclusive
                  onChange={handleSpeedChange}
                  size="small"
                  aria-label="Playback speed"
                >
                  {PLAYBACK_SPEEDS.map((rate) => (
                    <ToggleButton
                      key={rate}
                      value={rate}
                      aria-label={`${rate}x speed`}
                      sx={{
                        px: 1.2,
                        py: 0.3,
                        fontSize: '0.75rem',
                        fontWeight: playbackRate === rate ? 700 : 400,
                        minWidth: 40,
                      }}
                    >
                      {rate === 1 ? '1×' : `${rate}×`}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
              
              {videoError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                  <Button 
                    onClick={handleRetryVideo} 
                    variant="outlined" 
                    size="small" 
                    sx={{ ml: 2 }}
                    startIcon={isRetrying ? <CircularProgress size={16} /> : <RefreshIcon />}
                    disabled={isRetrying}
                    aria-label="Retry loading video"
                  >
                    Retry
                  </Button>
                </Alert>
              )}
            </Box>
          ) : (
            <Alert severity="error">Failed to load video URL.</Alert>
          )}
          
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Tooltip title="Download the original file at normal speed">
              <span>
                <Button 
                  variant="contained" 
                  startIcon={<DownloadIcon />} 
                  onClick={handleDownload}
                  disabled={isLoading || isDownloading}
                  aria-label="Download video at original speed"
                >
                  {isDownloading ? <CircularProgress size={24} /> : 'Download'}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Re-encode and download at a custom playback speed (client-side, uses FFmpeg)">
              <span>
                <Button
                  variant="outlined"
                  startIcon={<SpeedIcon />}
                  onClick={() => setSpeedDownloadOpen(true)}
                  disabled={isLoading || !decodedKey}
                  aria-label="Download at custom speed"
                >
                  Download at speed…
                </Button>
              </span>
            </Tooltip>
            <Button 
              variant="outlined" 
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              aria-label="Back to previous page"
            >
              Back to List
            </Button>
          </Stack>

          <SpeedDownloadDialog
            open={speedDownloadOpen}
            onClose={() => setSpeedDownloadOpen(false)}
            s3Key={decodedKey}
            filename={displayFileName.replace(/\.[^.]+$/, '')}
            durationSeconds={videoDuration}
          />
        </Paper>
      </Box>
    </Container>
  );
};

// Export a wrapper component that routes based on file type
const VideoPlayerWithRouter: React.FC = () => {
  const { key } = useParams<{ key: string }>();
  const decodedKey = key ? decodeURIComponent(key) : '';
  
  // Early check - if not a video file, use the redirect router
  const fileExtension = decodedKey.split('.').pop()?.toLowerCase() || '';
  const isVideoFile = ['mp4', 'mov', 'webm', 'avi', "mkv"].includes(fileExtension);
  
  if (!isVideoFile) {
    return <ImmediateRedirectRouter />;
  }
  
  // If it's a video file, use the regular VideoPlayer component
  return <VideoPlayer />;
};

export default VideoPlayerWithRouter;